import { useCallback } from 'react'

import { getWalletClient } from '@wagmi/core'
import { useAccount, useChainId, usePublicClient } from 'wagmi'

import { GasParams, AirdropList, Strategies, Token, Hash } from 'types/entities'

import { useAirdropper } from 'hooks/airdrop'
import { CreatePrivateKeyTransactionParams, useCreateTransactionByPrivateKey, usePrivateKeyWallet } from 'hooks/wallet'

import { usePrivateKey, useSelfGeneratedAddress, useUpdateTransactionHistory } from 'state/user/hooks'

import { logger } from 'utils/logger'
import { parseErrorToUserReadableMessage } from 'utils/errors'

import { errors } from 'constants/errors'
import { TxStatuses, TxTypes } from 'constants/transaction'

type ParamsWithSignature = {
  token: Token
  signature: Hash
  batchSize: bigint
  airdropId: string
  expiration: string
  gasParams: GasParams
  strategy: Strategies
  isDeflationary: boolean
  airdropList: AirdropList
  airdropChainId: SupportedChainId
}

function useSendTxByPrivateKey() {
  const chainId = useChainId()
  const { address: customer } = useAccount()

  const onUpdateTx = useUpdateTransactionHistory()
  const createTxByPrivateKey = useCreateTransactionByPrivateKey()

  const onSend = useCallback(
    async (
      txParams: CreatePrivateKeyTransactionParams,
      params: Omit<ParamsWithSignature, 'gasParams'>,
      retryAttempt = 0,
    ): Promise<string> => {
      try {
        if (!customer) {
          throw new Error(errors.wallet.WALLET_REQUIRED)
        }

        const txHash = await createTxByPrivateKey(txParams)
        onUpdateTx({
          txHash,
          chainId,
          from: customer,
          to: txParams.to,
          token: params.token,
          airdropId: params.airdropId,
          value: txParams.amount?.toString(),
          type: TxTypes.AIRDROP_WITH_SIGNATURE,
          status: TxStatuses.PENDING,
        })

        return txHash
      } catch (err) {
        if (retryAttempt < 3) {
          retryAttempt++
          const result = await onSend(txParams, params, retryAttempt)
          return result
        }
        throw new Error(parseErrorToUserReadableMessage(err))
      }
    },
    [createTxByPrivateKey, chainId, customer, onUpdateTx],
  )

  return onSend
}

export function useAirdropWithSignature() {
  const chainId = useChainId()
  const { address } = useAccount()
  const publicClient = usePublicClient()

  const getWallet = usePrivateKeyWallet()
  const selfGeneratedAddress = useSelfGeneratedAddress()

  const airdropper = useAirdropper()
  const privateKey = usePrivateKey()
  const sendTxByPrivateKey = useSendTxByPrivateKey()

  // https://github.com/mad-assembly/token-airdropper/blob/main/store/distribution.js#L826
  return useCallback(
    async (params: ParamsWithSignature) => {
      try {
        if (!selfGeneratedAddress || !privateKey) {
          throw new Error(errors.validation.PK_IS_REQUIRED)
        }

        const walletClient = await getWalletClient({ chainId })

        if (!address || !walletClient) {
          throw new Error(errors.wallet.WALLET_REQUIRED)
        }

        const txsData = await airdropper.createAirdropWithSignature(
          {
            customer: address,
            token: params.token,
            signature: params.signature,
            airdropList: params.airdropList,
            batchSize: BigInt(params.batchSize),
            isDeflationary: params.isDeflationary,
            expiration: BigInt(params.expiration),
          },
          selfGeneratedAddress,
        )

        const pkWallet = getWallet(privateKey)

        const txHashes = []
        let nonce = await publicClient.getTransactionCount({ address: pkWallet.account.address })
        for (const txData of txsData) {
          const txHash = await sendTxByPrivateKey(
            {
              ...txData,
              nonce,
              amount: txData.value,
              calldata: txData.data,
              gasParams: params.gasParams,
            },
            params,
          )

          txHashes.push(txHash)

          nonce++
        }

        return txHashes
      } catch (err) {
        logger.error('create airdrop error: ', err.message)

        const [errors]: string[] = Array(err.message.toString())
        const [text] = errors.split(`\n`)

        throw new Error(text)
      }
    },
    [address, getWallet, privateKey, airdropper, sendTxByPrivateKey, selfGeneratedAddress],
  )
}
